/**
 * Escape a `$` that can be neither an opening nor a closing maths delimiter.
 *
 * A TSA table header reads `<th>retail price ($)</th><th>auction site ($)</th>`.
 * Neither dollar is an amount, so currency escaping leaves them, MathJax pairs
 * them, and the markup between the two cells is pulled into an equation. The
 * table loses a column. A TSA explanation has the same problem with three
 * trailing `$` before `</p>`.
 *
 * Walks the dollars left to right the way MathJax pairs them. A maths
 * expression never begins with a closing bracket, a comma, a full stop or an
 * HTML tag, so a dollar followed by one of those cannot open a span. If nothing
 * is open for it to close either, it is punctuation and gets escaped.
 *
 * A closing delimiter is normally followed by punctuation, so this only ever
 * fires when no span is open. `$x^2$</p>` keeps both dollars, because the
 * second one closes what the first opened.
 */
// Deliberately narrow. TMUA opens a maths array with `<p>$<br> \begin{array}`,
// so a `<` is fine after an opening dollar; only a closing tag is not. A full
// stop is left out too, because `$.5$` is a legal if unusual span.
const CANNOT_START_MATHS = /[)\]},;:!?%]/;

export function escapeDollarsThatCannotDelimit(text) {
  if (typeof text !== 'string' || !text.includes('$')) return text || '';

  const positions = [];
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '$') continue;
    if (text[i - 1] === '\\') continue;
    positions.push(i);
  }
  if (!positions.length) return text;

  const escapeAt = new Set();
  let open = false;
  positions.forEach((i) => {
    if (open) {
      open = false;
      return;
    }
    const next = text[i + 1] || '';
    const closingTag = text.startsWith('</', i + 1);
    if (next === '' || closingTag || CANNOT_START_MATHS.test(next)) {
      escapeAt.add(i);
      return;
    }
    open = true;
  });
  if (!escapeAt.size) return text;

  let out = '';
  let prev = 0;
  positions.forEach((i) => {
    if (!escapeAt.has(i)) return;
    out += `${text.slice(prev, i)}\\$`;
    prev = i + 1;
  });
  return out + text.slice(prev);
}
